import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface TimerEntryEvent {
    jobId: number;
    jobNumber: string | number;
    jobTitle: string;
    clientName: string;
    description: string;
    start: Date;
    isBillable?: boolean;
}

export interface ContinueEntryData {
    jobId: number;
    jobNumber: string | number;
    jobTitle: string;
    clientName: string;
    description: string;
    isBillable?: boolean;
}

export interface LiveJobUpdate {
    jobId: number;
    jobNumber: string | number;
    jobTitle: string;
    clientName: string;
    isBillable?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class TimerEntryService {
    private _newEntry$ = new Subject<TimerEntryEvent>();
    private _stopEntry$ = new Subject<void>();
    private _updateDescription$ = new Subject<string>();
    private _updateStartTime$ = new Subject<Date>();
    private _continueEntry$ = new Subject<ContinueEntryData>();
    private _updateLiveJob$ = new Subject<LiveJobUpdate>();
    private _updateLiveEntryId$ = new Subject<number>();
    private _longTimerDismissed$ = new Subject<void>();

    /** Whether a live timer is currently running from the header */
    isLiveTimerRunning = false;

    /** Shared start time for the running timer — both header and calendar read this */
    liveStartTime: Date | null = null;

    /** The jobId of the currently running live timer */
    liveJobId: number | null = null;

    /** The full entry data for the currently running timer */
    liveEntry: TimerEntryEvent | null = null;

    /** Entries that were finalized while the timer page wasn't open */
    pendingFinalizedEntries: { entry: TimerEntryEvent; end: Date }[] = [];

    /** Mirror of the server's minimum-recorded-duration threshold (10 s).
     *  Entries shorter than this are deleted by the server on stop, so
     *  they shouldn't be queued for replay onto the calendar either —
     *  doing so would briefly re-create a "ghost" entry that the next
     *  poll-tick wipes again. Keep in sync with the server-side
     *  `MIN_RECORDED_DURATION_SECONDS` constant. */
    static readonly MIN_RECORDED_DURATION_MS = 10_000;

    /** Duration (ms) of the most recently stopped live timer, captured in
     *  `stopEntry()` BEFORE liveStartTime/liveEntry are cleared. Consumed
     *  by `timer-tracker.finalizeLiveTimer` to decide whether the entry
     *  should be discarded (< MIN_RECORDED_DURATION_MS) — reading the
     *  FullCalendar event's `start` from inside finalize is unreliable
     *  because the FC event may have been re-fetched/re-created during the
     *  timer's lifetime (id-swap on the server's timeEventId response,
     *  silent poll-tick rebuild, etc.), and any of those paths can leave
     *  `fcEvent.start` momentarily out of sync. Using the service-side
     *  number, computed from the same `liveEntry.start` that the server
     *  also sees, removes that fragility. -1 means "no recent stop /
     *  not applicable" so finalize can fall back to the FC event. */
    lastStoppedDurationMs = -1;

    /** Long-running timer alert — global state */
    readonly LONG_TIMER_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours
    showLongTimerAlert = false;
    /** Next elapsed-ms threshold at which the alert should fire */
    nextAlertAtMs = 0;

    /** Timestamp (ms) of the last local timer mutation — start, stop or
     *  job switch. The header's `applyRemoteTimerState` polls the backend
     *  to reconcile, and within a short guard window after a local
     *  mutation it skips reconciliation so an in-flight HTTP stop/start
     *  doesn't get reverted by stale poll data. Recording the timestamp
     *  on the service (not just the header) means modals that mutate the
     *  timer directly — e.g. crash-recovery's "Stop at last activity" —
     *  also get the guard, preventing the long-timer alert from briefly
     *  flashing as the poll re-anchors state on a 4h+ entry. */
    lastLocalMutationAt = 0;

    /** Count of HTTP requests to the running-timer endpoints (start time,
     *  job/desc/billable, stop) that are currently in flight. While this
     *  is > 0 we know the local optimistic state may not yet be reflected
     *  on the server, so any poll-tick fetch could return stale data and
     *  revert the user's just-made change. The header's
     *  `applyRemoteTimerState` consults this flag and skips reconciliation
     *  until all in-flight saves resolve.
     *
     *  More robust than the time-based `lastLocalMutationAt` guard alone:
     *  that one expires regardless of whether the server has caught up.
     *  This flag stays raised across slow API roundtrips (especially
     *  visible when the long-timer modal pops on a >4h start edit and
     *  the user lingers on the modal for several seconds — long enough
     *  for the time guard to expire while the PUT is still in flight). */
    runningTimerSaveInFlight = 0;

    get newEntry$(): Observable<TimerEntryEvent> {
        return this._newEntry$.asObservable();
    }

    get stopEntry$(): Observable<void> {
        return this._stopEntry$.asObservable();
    }

    get updateDescription$(): Observable<string> {
        return this._updateDescription$.asObservable();
    }

    get updateStartTime$(): Observable<Date> {
        return this._updateStartTime$.asObservable();
    }

    get continueEntry$(): Observable<ContinueEntryData> {
        return this._continueEntry$.asObservable();
    }

    get updateLiveJob$(): Observable<LiveJobUpdate> {
        return this._updateLiveJob$.asObservable();
    }

    get updateLiveEntryId$(): Observable<number> {
        return this._updateLiveEntryId$.asObservable();
    }

    /** Emits when the long-running-timer alert is dismissed (Keep running
     *  or Stop). `TimerSyncService` subscribes and broadcasts so sibling
     *  windows in the same browser instance can clear their alert too —
     *  cross-process sync (browser tab ↔ desktop app) goes through the
     *  server-side dismissal tracker instead. */
    get longTimerDismissed$(): Observable<void> {
        return this._longTimerDismissed$.asObservable();
    }

    /** Dismiss the long-running-timer alert locally. Resets the in-modal
     *  flag and advances `nextAlertAtMs` past the current threshold
     *  window so it won't fire again until the next interval boundary.
     *  Emits `longTimerDismissed$` so cross-window subscribers can mirror
     *  the change. Does NOT call the server — that's the caller's job
     *  (so we don't loop when receiving a remote dismissal). */
    dismissLongTimerAlert(): void {
        this.showLongTimerAlert = false;
        const start = this.liveStartTime;
        if (start) {
            const elapsedMs = Date.now() - start.getTime();
            const intervals = Math.floor(elapsedMs / this.LONG_TIMER_THRESHOLD_MS);
            this.nextAlertAtMs = (intervals + 1) * this.LONG_TIMER_THRESHOLD_MS;
        }
        this._longTimerDismissed$.next();
    }

    addEntry(entry: TimerEntryEvent): void {
        this.isLiveTimerRunning = true;
        this.liveStartTime = entry.start;
        this.liveJobId = entry.jobId;
        this.liveEntry = entry;
        this.nextAlertAtMs = this.LONG_TIMER_THRESHOLD_MS;
        this.showLongTimerAlert = false;
        this.lastLocalMutationAt = Date.now();
        this._newEntry$.next(entry);
    }

    stopEntry(): void {
        // Save finalized entry for the timer page to pick up if it wasn't
        // mounted — but only when the entry would actually have been
        // recorded server-side. Sub-10-second timers are discarded by the
        // backend, so queueing them here would create a ghost on the
        // calendar the moment the user navigates back, until the next
        // poll-tick refetch wipes it.
        if (this.liveEntry) {
            const end = new Date();
            const liveStart = this.liveEntry.start instanceof Date
                ? this.liveEntry.start
                : new Date(this.liveEntry.start as any);
            const durationMs = end.getTime() - liveStart.getTime();
            // Snapshot the duration so subscribers (timer-tracker's
            // finalizeLiveTimer in particular) can decide whether to
            // discard the entry without having to re-derive it from the
            // FC event — see the field comment for why FC's start can be
            // unreliable.
            this.lastStoppedDurationMs = durationMs;
            if (durationMs >= TimerEntryService.MIN_RECORDED_DURATION_MS) {
                this.pendingFinalizedEntries.push({
                    entry: { ...this.liveEntry },
                    end,
                });
            }
        } else {
            this.lastStoppedDurationMs = -1;
        }
        this.isLiveTimerRunning = false;
        this.liveStartTime = null;
        this.liveJobId = null;
        this.liveEntry = null;
        // Clear the long-running-timer alert state so the modal disappears
        // when a timer is stopped programmatically (e.g. crash-recovery's
        // "Stop at last activity" or idle-resolution's "Stop at idle start").
        // The user's manual button on the long-timer modal already clears
        // this; doing it here covers all other stop paths. Mirrors the
        // reset that addEntry does on the way in.
        this.showLongTimerAlert = false;
        this.nextAlertAtMs = this.LONG_TIMER_THRESHOLD_MS;
        this.lastLocalMutationAt = Date.now();
        this._stopEntry$.next();
    }

    updateDescription(description: string): void {
        if (this.liveEntry) { this.liveEntry = { ...this.liveEntry, description }; }
        this._updateDescription$.next(description);
    }

    updateStartTime(start: Date): void {
        this.liveStartTime = start;
        if (this.liveEntry) { this.liveEntry = { ...this.liveEntry, start }; }
        this._updateStartTime$.next(start);
    }

    continueEntry(data: ContinueEntryData): void {
        this._continueEntry$.next(data);
    }

    updateLiveEntryId(timeEventId: number): void {
        this._updateLiveEntryId$.next(timeEventId);
    }

    updateLiveJob(data: LiveJobUpdate): void {
        this.liveJobId = data.jobId;
        if (this.liveEntry) {
            this.liveEntry = {
                ...this.liveEntry,
                jobId: data.jobId,
                jobNumber: data.jobNumber,
                jobTitle: data.jobTitle,
                clientName: data.clientName,
            };
        }
        this._updateLiveJob$.next(data);
    }
}
